import PermissionButton from '../../../permissions/PermissionButton';
import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Space, Button, Tag, Spin, Alert, message, Modal } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import NestedAchatTable from '../NestedAchatTable';
import {
  useFetchFactureAchatById,
  useFetchItemsInFactureAchat,
  useValidateFactureAchat,
  useUnvalidateFactureAchat,
  useSaveFactureAchat,
} from '../../../hooks/achatFactureHooks';
import EditableDateField from '../../../utils/EditableDateField';
import { formatAmount } from '../../../utils/formatAmount';

const SingleFactureAchat = ({ id, onStatusChange, onClose }) => {
  const { data, loading, refetch } = useFetchFactureAchatById(id);
  const { items, loading: itemsLoading, refetch: refetchItems } = useFetchItemsInFactureAchat(id);
  const { validate, loading: validateLoading } = useValidateFactureAchat();
  const { unvalidate, loading: unvalidateLoading } = useUnvalidateFactureAchat();
  const { save, loading: saveLoading } = useSaveFactureAchat();
  const [isClosing, setIsClosing] = useState(false);
  const [localFacture, setLocalFacture] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setLocalFacture({ ...data });
      setHasLocalChanges(false);
    }
  }, [data]);

  // Empty functions since facture lines are read-only
  const handleSave = () => {
    console.log('Facture achat table is read-only');
  };

  const handleDelete = () => {
    console.log('Facture achat table is read-only');
  };

  const handleSaveDate = async () => {
    if (!localFacture) return;
    try {
      const details = (items || []).map(({ article, ...it }) => ({ ...it }));
      await save(localFacture, details);
      setHasLocalChanges(false);
      message.success('Date enregistrée');
      await refetch();
      await refetchItems();
      onStatusChange && onStatusChange();
    } catch (error) {
      message.error(`Échec de l'enregistrement: ${error?.message || String(error)}`);
    }
  };

  const doValidate = async () => {
    Modal.confirm({
      title: 'Confirmer la validation',
      content: 'Êtes-vous sûr de vouloir valider cette facture d\'achat?',
      okText: 'Oui, valider',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await validate(id);
          message.success('Facture d\'achat validée avec succès');
          await refetch();
          await refetchItems();
          onStatusChange && onStatusChange();
        } catch (error) {
      message.error(`Échec de la validation: ${error?.message || String(error)}`); }
      }
    });
  };

  const doUnvalidate = async () => {
    Modal.confirm({
      title: 'Confirmer la dévalidation',
      content: 'Êtes-vous sûr de vouloir dévalider cette facture d\'achat?',
      okText: 'Oui, dévalider',
      cancelText: 'Annuler',
      okType: 'danger',
      onOk: async () => {
        try {
          await unvalidate(id);
          message.success('Facture d\'achat dévalidée avec succès');
          await refetch();
          await refetchItems();
          onStatusChange && onStatusChange();
        } catch (error) {
         message.error(`Échec de la dévalidation: ${error?.message || String(error)}`);  }
      }
    });
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose && onClose(); }, 120);
  };

  if (loading || !localFacture) return <Spin tip="Chargement..." style={{ display: 'block', margin: '50px auto' }} />;
  if (!data) return <Alert message="Facture d'achat introuvable" type="error" showIcon />;

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.15, ease: [0.25, 0.1, 0.25, 1.0] } },
    exit: { opacity: 0, scale: 0.97, transition: { duration: 0.12, ease: [0.25, 0.1, 0.25, 1.0] } }
  };

  return (
    <motion.div initial="hidden" animate={isClosing ? "exit" : "visible"} variants={containerVariants}>
      <Card
        title={
          <>
            Facture d'Achat - {localFacture.REF_FAC_A}
            {localFacture.VALIDER ? <Tag color="green" style={{ marginLeft: 8 }}>Validé</Tag> : <Tag color="orange" style={{ marginLeft: 8 }}>Enregistré</Tag>}
            <Tag color={localFacture?.CODE_ENT ? "blue" : "default"} style={{ marginLeft: 8 }}>
              Entrepôt: {localFacture?.CODE_ENT 
                ? (localFacture.entrepot ? `${localFacture.entrepot.code_ent} - ${localFacture.entrepot.name}` : localFacture.CODE_ENT)
                : "Non défini"}
            </Tag>
          </>
        }
        bordered={false}
        extra={
          <Space>
            {hasLocalChanges && (
              <PermissionButton permissionModule="purchaseInvoices" permissionAction="update" type="primary" onClick={handleSaveDate} loading={saveLoading}>
                Enregistrer
              </PermissionButton>
            )}
            {!localFacture.VALIDER && (
              <PermissionButton permissionModule="purchaseInvoices" permissionAction="validate" type="primary" icon={<CheckOutlined />} onClick={doValidate} loading={validateLoading} disabled={hasLocalChanges}>
                Valider
              </PermissionButton>
            )}
            {localFacture.VALIDER && (
              <PermissionButton permissionModule="purchaseInvoices" permissionAction="unvalidate" danger icon={<CloseOutlined />} onClick={doUnvalidate} loading={unvalidateLoading}>
                Dévalider
              </PermissionButton>
            )}
            <Button onClick={handleClose} style={{ backgroundColor: 'white', color: '#EE2625', border: '1px solid #EE2625' }}>SORTIR</Button>
          </Space>
        }
      >
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Date">
            <EditableDateField
              value={localFacture.DATE_FAC}
              onChange={(newDate) => {
                setLocalFacture((prev) => ({ ...prev, DATE_FAC: newDate }));
                setHasLocalChanges(true);
              }}
              disabled={!!localFacture.IsCanceled}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Fournisseur">{localFacture.FOURNISSEUR || '-'}</Descriptions.Item>
          <Descriptions.Item label="Ref. Bon Récep.">{localFacture.REF_BR_A || '-'}</Descriptions.Item>
          <Descriptions.Item label="Code Fournisseur">{localFacture.CODE_FRS || '-'}</Descriptions.Item>
          <Descriptions.Item label="Montant HT">{formatAmount(localFacture.MNT_HT)} DH</Descriptions.Item>
          <Descriptions.Item label="Montant TTC">{formatAmount(localFacture.MNT_TTC)} DH</Descriptions.Item>
          <Descriptions.Item label="Remarque" span={2}>{localFacture.REMARQUE || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title={`Articles (${items?.length || 0})`} loading={itemsLoading} style={{ marginTop: 20 }}>
        <NestedAchatTable
          items={items || []}
          handleSave={handleSave}
          handleDelete={handleDelete}
          validated={true}
          userPermissions={{ isManager: false }}
          type="facture"
          codeEnt={localFacture?.CODE_ENT}
          showStock={true}
        />
      </Card>
    </motion.div>
  );
};

export default SingleFactureAchat;
